<script setup lang="ts">
    import EmployeeAvatar from '@/components/EmployeeAvatar.vue';
    import type { Employee } from '@/types';
    import {
        Dialog,
        DialogClose,
        DialogContent,
        DialogBody,
        DialogFooter,
        DialogHeader,
        DialogTitle,
        DialogTrigger,
    } from '@/components/ui/dialog';
    import { Button } from '@/components/ui/button';
    import { useI18n } from 'vue-i18n';
    import ContinueButton from '@/components/ContinueButton.vue';
    import EmployeeChangesTabs from '@/components/employees/EmployeeChangesTabs.vue';
    import axios from 'axios';
    import Permission from '@/enums/Permission';
    import { usePermissions } from '@/composables/usePermissions';
    import { useMutation } from '@tanstack/vue-query';
    import { toast } from 'vue-sonner';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { computed, ref } from 'vue';

    const { t } = useI18n();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const queryClient = useQueryClient();
    const isOpen = ref(false);

    const props = defineProps<{
        employee: Employee;
    }>();

    const { hasPermission } = usePermissions();

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            return axios.post(route('v2.salary.employee.changes.noted', { id: props.employee.id }));
        },
        onSuccess: (data) => {
            toast.success(t('forms.general.success'));
            queryClient.invalidateQueries({ queryKey: [QueryKey.EMPLOYEES, selectedCompanyId] });
            isOpen.value = false;
        },
        onError: (error: any) => {
            toast.error(t('forms.system_error'));
        },
    });

    const changesNoted = () => {
        mutate();
    };
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <ContinueButton>
                {{ t('forms.open') }}
            </ContinueButton>
        </DialogTrigger>
        <DialogContent class="sm:max-w-3xl">
            <DialogHeader>
                <DialogTitle>{{ t('employee.table.changes') }}</DialogTitle>
            </DialogHeader>

            <DialogBody>
                <EmployeeChangesTabs :employee="employee" />
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
                <Button
                    v-if="
                        hasPermission(Permission.EMPLOYEES_UPDATE) &&
                        ((employee.log && !employee.log.changes_noted) || employee.unnoted_salary)
                    "
                    size="sm"
                    @click="changesNoted"
                    :disabled="isPending"
                    :loading="isPending"
                >
                    {{ t('forms.note_change') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
